import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { PRODUCTS_AND_SERVICES_ADVANTAGES_NAMES } from 'components/BusinessToolForm/Sections/ProductsAndServices/ProductAndServicesAdvantages/utils/form';
import { PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES } from 'components/BusinessToolForm/Sections/ProductsAndServices/ProductAndServicesDeliveryAndSupport/utils/form';
import {
  KEY_FEATURES_NAMES,
  PRODUCT_AND_SERVICES_ARRAY_NAMES,
  PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES,
} from 'components/BusinessToolForm/Sections/ProductsAndServices/ProductAndServicesDescription/utils/form';
import {
  PRODUCTS_AND_SERVICES_R_AND_D_NAMES,
  TIMELINE_ROW_NAMES,
} from 'components/BusinessToolForm/Sections/ProductsAndServices/ProductAndServicesRAndD/utils/form';

import { ol, ul } from '../helpers';

const KEY_FEATURES_INSTRUCTIONS = {
  [KEY_FEATURES_NAMES.FEATURE]: {
    name: 'Características del producto o servicio',

    instruction:
      'Enumere las principales características del producto o servicio, destacando de forma clara sus funcionalidades, ventajas y atributos diferenciales.',

    exampleOne: '',
    exampleTwo: '',
  },

  [KEY_FEATURES_NAMES.DESCRIPTION]: {
    name: 'Descripción de las características',

    instruction:
      'Describa los elementos innovadores, las tecnologías utilizadas y los aspectos que diferencian su producto o servicio de las alternativas existentes en el mercado. Explique sus funcionalidades, especificaciones y características de diseño que generan una ventaja competitiva.',

    exampleOne: ol([
      'Alto rendimiento: diseñado para ofrecer un desempeño fiable incluso en las condiciones de trabajo más exigentes.',
      'Sistema de orugas continuo: proporciona una tracción superior y reduce la compactación del suelo, especialmente en terrenos pesados o húmedos.',
      'Revestimiento de caucho patentado: disminuye la presión sobre el terreno y protege los cultivos, mejorando la eficiencia operativa.',
    ]),

    exampleTwo: ol([
      'Precisión: los servicios de traducción y las herramientas impulsadas por inteligencia artificial garantizan la máxima fidelidad al significado original.',
      'Eficiencia: los algoritmos avanzados de IA aceleran el proceso de traducción sin comprometer la calidad, mejorando la productividad de los usuarios.',
    ]),
  },
};

const DEVELOPMENT_TIMELINE_INSTRUCTIONS = {
  [TIMELINE_ROW_NAMES.STAGE]: {
    name: 'Cronograma de desarrollo',

    instruction:
      'Al describir el cronograma del proyecto, incluya:' +
      ol([
        'Las principales etapas desde la concepción de la idea hasta el lanzamiento.',
        'La duración estimada de cada etapa.',
        'La fase en la que se encuentra actualmente el proyecto.',
      ]),

    exampleOne: ul([
      'Concepto y diseño: 6 meses.',
      'Prototipado y pruebas: 9 meses.',
      'Preparación de la producción: 3 meses.',
      'Marketing y preparación del lanzamiento: 2 meses.',
      'Estado actual: preparación de la producción.',
    ]),

    exampleTwo: ul([
      'Conceptualización: 3 meses.',
      'Diseño y desarrollo de prototipos: 6 meses.',
      'Pruebas y optimización: 4 meses.',
      'Producción y lanzamiento: 3 meses.',
      'Estado actual: fase de pruebas y optimización.',
    ]),
  },
};

const KEY_FEATURES_AND_BENEFITS_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: KEY_FEATURES_INSTRUCTIONS,

  [PRODUCT_AND_SERVICES_ARRAY_NAMES.FILE]: {
    name: '',

    instruction:
      'Suba una imagen de alta calidad del producto o servicio. Asegúrese de que tenga una iluminación adecuada y una presentación profesional.',

    exampleOne: '',
    exampleTwo: '',
  },

  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Nombre del producto o servicio',

    instruction:
      'Indique el nombre comercial del producto o servicio utilizando una denominación clara, profesional y representativa de la oferta.',

    exampleOne: '',
    exampleTwo: '',
  },
};

const QUALITY_CERTIFICATIONS_DESCRIPTION_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Certificación de calidad',

    instruction:
      'Enumere las certificaciones de calidad relevantes, como ISO 9001, indicando el nombre oficial de cada una.',

    exampleOne: 'EcoGrain Certified',
    exampleTwo: 'EnviroCrop Certified',
  },

  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: {
    name: 'Descripción de la certificación',

    instruction:
      'Describa brevemente cada certificación, explicando su alcance, importancia y el valor que aporta al producto, al servicio o a la empresa.',

    exampleOne:
      'EcoGrain Certified acredita que los productos se obtienen mediante prácticas agrícolas sostenibles, incluyendo agricultura ecológica, uso responsable del agua y reducción de pesticidas.',

    exampleTwo:
      'EnviroCrop Certified garantiza que la producción cumple criterios de sostenibilidad ambiental, conservación del suelo y protección de los recursos naturales.',
  },
};

const PARENTS_OR_TRADEMARKS_DESCRIPTION_INSTRUCTIONS = {
  [PRODUCT_AND_SERVICES_ARRAY_NAMES.VALUE]: {
    name: 'Propiedad intelectual',

    instruction:
      'Enumere las principales patentes, marcas registradas u otros activos de propiedad intelectual, indicando su nombre oficial y, cuando corresponda, su número de registro.',

    exampleOne: 'Tecnología InnoGrain™',
    exampleTwo: 'Tecnología SmartSeed®',
  },

  [PRODUCT_AND_SERVICES_ARRAY_NAMES.DESCRIPTION]: {
    name: 'Descripción de la propiedad intelectual',

    instruction:
      'Explique la importancia estratégica de cada patente o marca registrada y describa cómo protege las innovaciones y fortalece la ventaja competitiva de la empresa.',

    exampleOne:
      'La tecnología patentada InnoGrain™ incorpora innovaciones en genética, cultivo y conservación de cereales que mejoran el rendimiento, la resistencia a enfermedades y la calidad nutricional.',

    exampleTwo:
      'La tecnología SmartSeed® integra innovaciones genéticas que incrementan la resistencia a plagas, optimizan la absorción de nutrientes y mejoran la productividad agrícola de forma sostenible.',
  },
};

const DESCRIPTION_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PRODUCTS_OR_SERVICES_AND_THEIR_PURPOSE]: {
    name: 'Portafolio de productos y servicios',

    instruction:
      'Presente una visión estructurada de su portafolio. Agrupe los productos o servicios por categorías o destaque las soluciones principales que definen la propuesta de valor de la empresa. Explique las características que los diferencian de la competencia, como la innovación, la calidad, la especialización o la tecnología.',

    exampleOne:
      'La empresa está especializada en el desarrollo de tractores de orugas para terrenos pesados y húmedos. Su portafolio incluye:' +
      ol(['Tractores de orugas', 'Maquinaria agrícola', 'Soluciones para agricultura sostenible']),

    exampleTwo:
      'La empresa ofrece un ecosistema de soluciones para optimizar la comunicación internacional, que incluye:' +
      ol([
        'Servicios de traducción',
        'Herramientas de traducción basadas en inteligencia artificial',
        'Soluciones para optimizar la comunicación empresarial',
        'Tecnologías lingüísticas innovadoras',
      ]),
  },

  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.KEY_FEATURES_AND_BENEFITS]: KEY_FEATURES_AND_BENEFITS_INSTRUCTIONS,

  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_STANDARDS]: {
    name: 'Estándares de calidad',

    instruction:
      'Explique cómo sus productos o servicios cumplen estándares internacionales de calidad, como ISO 9001:2015, e indique cualquier otra norma específica aplicable a su sector.',

    exampleOne:
      'Los productos cumplen estándares internacionales de calidad como ISO 9001:2015 y normas específicas del sector para garantizar la excelencia en los procesos de fabricación y el rendimiento del producto.',

    exampleTwo:
      'Los productos y servicios cumplen estándares internacionales como ISO 9001:2015 y otras normas especializadas de aseguramiento de la calidad aplicables al sector de los servicios lingüísticos.',
  },

  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_CERTIFICATIONS]: QUALITY_CERTIFICATIONS_DESCRIPTION_INSTRUCTIONS,

  [PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PATENTS_OR_TRADEMARKS]: PARENTS_OR_TRADEMARKS_DESCRIPTION_INSTRUCTIONS,
};
const ADVANTAGES_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_ADVANTAGES_NAMES.PRODUCT_VALUE]: {
    name: 'Valor para el cliente',

    instruction:
      'Explique claramente el valor principal que aporta su producto o servicio y el problema que resuelve. Destaque los beneficios concretos para el cliente, indicando cómo la solución mejora su eficiencia, reduce costes, ahorra tiempo o genera una mejor experiencia.',

    exampleOne:
      'Al reducir la compactación del suelo y maximizar la tracción, los tractores de orugas permiten a los agricultores trabajar con mayor eficiencia, aumentar la productividad y optimizar los costes operativos.',

    exampleTwo:
      'La plataforma de servicios lingüísticos elimina las barreras de comunicación entre empresas y clientes internacionales, facilitando una expansión global más rápida y eficiente.',
  },
};

const R_AND_D_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.READINESS_LEVEL]: {
    name: 'Nivel de preparación para la producción y el lanzamiento',

    instruction:
      'Indique el estado actual de desarrollo del producto o servicio utilizando una descripción clara. Por ejemplo: "Lanzado al mercado", "Fase final de pruebas", "En desarrollo" o "Fase conceptual".',

    exampleOne:
      'El producto ya está lanzado al mercado, disponible para su comercialización y respaldado por pruebas de campo que validan su rendimiento y fiabilidad.',

    exampleTwo:
      'La aplicación de traducción se encuentra actualmente en desarrollo y su lanzamiento está previsto para dentro de seis meses.',
  },

  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.CONDUCTED_ACTIVITIES]: {
    name: 'Avance de I+D',

    instruction:
      'Resuma las principales actividades de investigación y desarrollo. Destaque las áreas de investigación, los objetivos alcanzados, las innovaciones obtenidas y los resultados más relevantes.',

    exampleOne:
      'Las actividades de I+D se han centrado en mejorar la eficiencia de los tractores, reducir el impacto ambiental y optimizar la salud del suelo. Entre los principales avances destaca el desarrollo de un sistema de orugas patentado.',

    exampleTwo:
      'Las actividades de I+D se orientan al desarrollo de algoritmos de inteligencia artificial para mejorar la precisión y la velocidad de las traducciones. Los últimos avances han permitido incrementar la eficiencia del sistema en un 20 %.',
  },

  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.TIMELINES]: DEVELOPMENT_TIMELINE_INSTRUCTIONS,

  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.RESOURCES]: {
    name: 'Recursos necesarios',

    instruction:
      'Describa los recursos clave necesarios para desarrollar, producir y comercializar el producto o servicio. Organice la información por categorías, como recursos técnicos, financieros, humanos y estratégicos.',

    exampleOne:
      'Los recursos esenciales incluyen equipamiento industrial avanzado, financiación para investigación y producción, ingenieros especializados, personal técnico y alianzas estratégicas con proveedores y organismos reguladores.',

    exampleTwo: ol([
      'Recursos técnicos: herramientas de desarrollo de software e infraestructura de IA.',
      'Recursos financieros: financiación para investigación, desarrollo y adquisición de tecnología.',
      'Recursos humanos: ingenieros de software, especialistas en IA y gestores de producto.',
      'Recursos estratégicos: estudios de mercado, asesoramiento jurídico y alianzas tecnológicas.',
    ]),
  },

  [PRODUCTS_AND_SERVICES_R_AND_D_NAMES.WEAKNESSES]: {
    name: 'Limitaciones del producto y plan de mejora',

    instruction:
      'Describa de forma transparente las principales limitaciones de la versión actual del producto o servicio. Explique cómo la empresa está abordando estas áreas de mejora, incluyendo las acciones previstas, los plazos estimados y el impacto esperado.',

    exampleOne:
      'La empresa trabaja en mejorar la eficiencia del consumo de combustible mediante la optimización del motor y de la aerodinámica. La implementación de estas mejoras está prevista para el próximo año.',

    exampleTwo:
      'Como respuesta a los comentarios de los usuarios sobre la precisión de determinadas traducciones, la empresa está optimizando sus modelos de inteligencia artificial con el objetivo de mejorar la precisión en un 15 % durante los próximos tres meses.',
  },
};

const DELIVERY_AND_SUPPORT_INSTRUCTIONS = {
  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.LOGISTICS]: {
    name: 'Logística y entrega',

    instruction:
      'Describa el proceso de entrega de sus productos o servicios. Incluya la gestión logística, las alianzas con operadores de transporte o centros de distribución y las medidas implementadas para garantizar entregas rápidas, fiables y una resolución ágil de cualquier incidencia.',

    exampleOne:
      'La empresa colabora con operadores logísticos internacionales para realizar envíos a todo el mundo. Los pedidos se procesan con rapidez, incluyen seguimiento en tiempo real y disponen de un procedimiento de devolución sencillo y eficiente.',

    exampleTwo:
      'Los pedidos se procesan en un plazo de 24 horas y se envían mediante transportistas especializados. El servicio de atención al cliente gestiona de forma prioritaria cualquier incidencia relacionada con la entrega.',
  },

  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.POST_PURCHASE]: {
    name: 'Servicios de soporte y mantenimiento',

    instruction:
      'Describa los servicios de asistencia disponibles después de la compra. Incluya soporte técnico, mantenimiento, recursos de autoayuda, documentación, centros de servicio y los canales a través de los cuales los clientes pueden obtener asistencia.',

    exampleOne:
      'La empresa ofrece soporte técnico integral, guías de resolución de incidencias, servicios de mantenimiento y centros de asistencia especializados. Los clientes pueden acceder al soporte mediante recursos en línea, líneas telefónicas dedicadas o centros de servicio autorizados.',

    exampleTwo:
      'Los clientes disponen de asistencia técnica 24/7, documentación en línea, guías de resolución de problemas y acceso a centros de servicio especializados para garantizar la continuidad operativa.',
  },

  [PRODUCTS_AND_SERVICES_DELIVERY_AND_SUPPORT_NAMES.ANY_WARRANTY]: {
    name: 'Garantía y política de devoluciones',

    instruction:
      'Describa las condiciones de la garantía ofrecida, incluyendo su duración, cobertura y posibles limitaciones. Explique también el proceso de devolución, los requisitos de elegibilidad, los plazos y el procedimiento para solicitar una devolución o un reembolso.',

    exampleOne:
      'Los productos cuentan con una garantía de 12 meses que cubre defectos de fabricación. Las devoluciones pueden solicitarse dentro de los 30 días posteriores a la compra para productos que cumplan las condiciones establecidas.',

    exampleTwo:
      'La plataforma ofrece una garantía de satisfacción que permite solicitar un reembolso durante los primeros 30 días. Además, dispone de una garantía técnica de 12 meses para incidencias relacionadas con el funcionamiento del servicio.',
  },
};

export const PRODUCTS_AND_SERVICES_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DESCRIPTION]: DESCRIPTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.ADVANTAGES]: ADVANTAGES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.R_AND_D]: R_AND_D_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.PRODUCTS_AND_SERVICES.DELIVERY_AND_SUPPORT]: DELIVERY_AND_SUPPORT_INSTRUCTIONS,
};
